/**
 * @file billQtyOnSave.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-16
 * @modified 2026-10-16
 * @description Add / Edit Bill, on save: the bill's quantity against what the
 *              PO says was supplied (Cam, 16 Oct 2026 — "on save offer user to
 *              update the PO's supplied qty with the Bill's qty if they want
 *              to, or leave them different and mark queried").
 *
 *   ASKED ONCE, in one window, for each PO line the bill bills where
 *     - a supplied quantity is recorded and the bill's differs from it, or
 *     - none is recorded (the bill's can be recorded) —
 *   unless the user has already answered on the line's note ("Use the bill's
 *   quantity as supplied" ticked, or "Use the PO's supplied qty" so the two
 *   agree) or the bill is already Queried and there is nothing else to do.
 *   A PO made in Xero has no supplied figure here: asked only when the bill
 *   is more than the query limit from what was ordered (0.5 T bulk, 1 bag)
 *   — a weighbridge's few kilos are not a question.
 *
 *   EACH LINE, ONE OF:
 *     copy   — Set the PO's supplied qty to the bill's (the existing
 *              `supplied_copies` path; greyed with the reason where the PO
 *              cannot take it — made in Xero, split between orders, the
 *              order's invoice gone out…). Far from what was ordered it is
 *              sent `confirmed` — choosing it here, with both figures named,
 *              is the confirmation the server asks for.
 *     query  — Leave it, and mark the bill Queried (Draft, here and in Xero).
 *     leave  — Leave it as it is.
 *   Starts on the first that can be taken — except a line with no supplied
 *   quantity whose bill is far from what was ordered: that starts on
 *   "mark Queried", because recording it needs a deliberate say-so.
 *
 *   Never a block: × goes back to the bill; Save saves with the choices.
 *
 *   Pure.
 */

import { formatQuantity } from './numberFormatters';

export const COPY = 'copy';
export const QUERY = 'query';
export const LEAVE = 'leave';

const num = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const same = (a, b) => a !== null && b !== null && Math.abs(a - b) < 0.00005;

/**
 * @typedef {object} QtyRow
 * @property {string} key            — `${poId}|${poLineKey}`
 * @property {number} poId
 * @property {string} poLineKey
 * @property {string} poNumber
 * @property {string} what           — the product, in words
 * @property {number|null} billQty   — the bill's, in the PO line's unit
 * @property {number|null} supplied  — recorded on the PO line (null: none)
 * @property {number|null} ordered
 * @property {boolean} [xeroMade]
 * @property {string|null} [copyBlock] — why supplied cannot be set from this bill
 * @property {'none'|'note'|'query'} [farFromOrdered] — the bill against what was ordered (utils/billQtyCheck)
 * @property {boolean} [answered]    — answered on the line's note already
 * @property {(n: number) => string} [format]
 */

/**
 * The lines to ask about, each with its choices and where it starts.
 * @param {QtyRow[]} rows
 * @param {{ queried?: boolean, canQuery?: boolean }} [opts] — queried: the bill is
 *        Queried already; canQuery: the bill's status may be changed here
 */
export function qtyQuestions(rows, { queried = false, canQuery = true } = {}) {
  const out = [];
  for (const r of rows || []) {
    if (!r || r.answered) continue;
    const bill = num(r.billQty);
    if (bill === null) continue;
    const supplied = num(r.supplied);
    const ordered = num(r.ordered);
    const fmt = r.format || ((n) => formatQuantity(n));
    const far = r.farFromOrdered === 'query';
    let ask;
    if (r.xeroMade) ask = far;
    else if (supplied !== null) ask = !same(bill, supplied);
    else ask = true;
    if (!ask) continue;
    const copyBlock = r.xeroMade ? (r.copyBlock || 'Made in Xero — there is no supplied quantity here') : (r.copyBlock || null);
    if (queried && copyBlock) continue; // nothing left to decide
    const options = [
      {
        value: COPY,
        label: `Set ${r.poNumber}'s supplied qty to the bill's (${fmt(bill)})`,
        disabled: !!copyBlock,
        title: copyBlock || undefined,
      },
      ...(queried ? [] : [{
        value: QUERY,
        label: 'Leave it, and mark the bill Queried',
        disabled: !canQuery,
        title: canQuery ? undefined : "This bill's status is not changed from here",
      }]),
      { value: LEAVE, label: queried ? 'Leave it as it is (the bill is Queried)' : 'Leave it as it is' },
    ];
    // Recording a bill far from what was ordered needs a deliberate say-so.
    const needsConfirm = far && !r.xeroMade;
    const firstOpen = options.find(o => !o.disabled);
    let initial = firstOpen ? firstOpen.value : LEAVE;
    if (needsConfirm && supplied === null && initial === COPY) {
      initial = options.some(o => o.value === QUERY && !o.disabled) ? QUERY : LEAVE;
    }
    const poSide = supplied !== null
      ? `${fmt(supplied)} supplied on ${r.poNumber}${ordered !== null ? ` (${fmt(ordered)} ordered)` : ''}`
      : (r.xeroMade
        ? `${ordered !== null ? fmt(ordered) : '—'} ordered on ${r.poNumber} (made in Xero — no supplied figure)`
        : `no supplied qty on ${r.poNumber} yet${ordered !== null ? ` (${fmt(ordered)} ordered)` : ''}`);
    out.push({
      key: r.key, poId: Number(r.poId), poLineKey: String(r.poLineKey), poNumber: r.poNumber, what: r.what || '',
      text: `Bill ${fmt(bill)} · ${poSide}`,
      warning: needsConfirm && ordered !== null
        ? `The bill says ${fmt(bill)}, ${fmt(ordered)} were ordered — record it as supplied only if the delivery (ticket or driver) says so.`
        : null,
      needsConfirm, options, initial,
    });
  }
  return out;
}

/**
 * What the answers come to.
 * @param {ReturnType<typeof qtyQuestions>} questions
 * @param {Object<string, string>} answers — key → copy | query | leave (absent: its initial)
 * @returns {{ copies: Array<{ poId: number, poLineKey: string, confirmed: boolean }>, markQueried: boolean, left: string[] }}
 */
export function qtyAnswers(questions, answers = {}) {
  const copies = [];
  const left = [];
  let markQueried = false;
  for (const q of questions || []) {
    const pick = answers[q.key] || q.initial;
    const opt = q.options.find(o => o.value === pick && !o.disabled);
    const v = opt ? opt.value : LEAVE;
    if (v === COPY) copies.push({ poId: q.poId, poLineKey: q.poLineKey, confirmed: !!q.needsConfirm });
    else if (v === QUERY) markQueried = true;
    else left.push(q.key);
  }
  return { copies, markQueried, left };
}
